import {
  FieldWrapper,
  FieldWrapperProps,
  FlexBox,
  InputText,
  Select,
} from '@dixa/lib-shared-habla';
import { useEffect, useState } from 'react';
import { useUnmount } from 'react-use';

import { useBreakpointValue } from 'utils/mediaQuery';

import styles from './authorizationHeader.module.scss';
import type {
  AuthorizationOption,
  AuthorizationType,
  BasicAuthentication,
} from './authorizationHeader.types';

type AuthorizationHeaderProps = {
  value?: string;
  onChange: (value?: string) => void;
  selectedAuthorizationType: AuthorizationType;
  setSelectedAuthorizationType: (type: AuthorizationType) => void;
  meta: FieldWrapperProps['meta'];
  disabled: boolean;
};

const authorizationOptions: AuthorizationOption[] = [
  { id: 'none', name: 'None' },
  { id: 'bearer', name: 'Bearer' },
  { id: 'basic', name: 'Basic' },
  { id: 'custom', name: 'Custom' },
];

const defaultBasicAuth: BasicAuthentication = {
  username: '',
  password: '',
};

const bearerPrefix = 'Bearer ';
const basicPrefix = 'Basic ';

export const AuthorizationHeader = ({
  value,
  onChange,
  selectedAuthorizationType,
  setSelectedAuthorizationType,
  meta,
  disabled,
}: AuthorizationHeaderProps): JSX.Element => {
  const [basicAuth, setBasicAuth] = useState(defaultBasicAuth);
  const [authorization, setAuthorization] = useState('');

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const handleBearerInitialization = (value: string) => {
    setSelectedAuthorizationType('bearer');
    setAuthorization(value.slice(bearerPrefix.length));
  };

  const handleBasicInitialization = (value: string) => {
    setSelectedAuthorizationType('basic');

    let basicValue = value;

    try {
      basicValue = window.atob(value.slice(basicPrefix.length));
    } catch {
      // Do nothing
    }

    const splitBasicValues = basicValue.split(':');

    if (splitBasicValues.length === 2) {
      setBasicAuth({
        username: splitBasicValues[0],
        password: splitBasicValues[1],
      });
    } else {
      // If splitting the decoded basic string by `:` does not result in 2
      // values, we assume the initial `value` is not based on an encoded
      // `username:password` structure and we handle it as a custom value.
      handleCustomInitialization(value);
    }
  };

  const handleCustomInitialization = (value: string) => {
    setSelectedAuthorizationType('custom');
    setAuthorization(value);
  };

  useEffect(() => {
    if (
      value &&
      basicAuth.password === '' &&
      basicAuth.username === '' &&
      authorization === ''
    ) {
      if (value.toLowerCase().startsWith(bearerPrefix.toLowerCase())) {
        handleBearerInitialization(value);
      } else if (value.toLowerCase().startsWith(basicPrefix.toLowerCase())) {
        handleBasicInitialization(value);
      } else {
        handleCustomInitialization(value);
      }
    }
  }, [value]);

  useUnmount(() => {
    onChange(undefined);
  });

  const validateBearer = (input: string): string | null => {
    if (input.toLowerCase().startsWith(bearerPrefix.toLowerCase())) {
      return 'Remove the leading `Bearer` - it is set automatically.';
    }
    return null;
  };

  return (
    <FlexBox gap={isSmallScreen ? '250' : '400'} flexWrap="wrap">
      <FieldWrapper
        disabled={disabled}
        label="Authorization header"
        helpIcon={{
          dark: true,
          title: 'Authorization header',
          text: 'Select the authorization type and enter the corresponding value. Use `custom` to have full control over the authorization value.',
        }}
        className={styles.authorization}
      >
        <Select
          disabled={disabled}
          items={authorizationOptions}
          onSelect={(item: AuthorizationOption) => {
            setAuthorization('');
            setBasicAuth(defaultBasicAuth);
            onChange(undefined);
            setSelectedAuthorizationType(item.id);
          }}
          selectedItems={[selectedAuthorizationType || authorizationOptions[0]]}
          className={styles.authorizationSelect}
        />
      </FieldWrapper>

      {selectedAuthorizationType === 'bearer' && (
        <FieldWrapper
          required
          disabled={disabled}
          label="Token"
          meta={{
            ...meta,
            warning: validateBearer(authorization),
          }}
        >
          <InputText
            disabled={disabled}
            status={
              validateBearer(authorization) ?? meta?.error ? 'error' : undefined
            }
            value={authorization}
            onChange={(event) => {
              setAuthorization(event.target.value);
              onChange(`${bearerPrefix}${event.target.value}`);
            }}
            placeholder="Input bearer token"
            prefix={{
              type: 'custom',
              value: 'Bearer',
            }}
          />
        </FieldWrapper>
      )}

      {selectedAuthorizationType === 'basic' && (
        <FlexBox flex="1" gap="200">
          <FieldWrapper
            disabled={disabled}
            required
            label="Username"
            meta={meta}
          >
            <InputText
              disabled={disabled}
              value={basicAuth.username}
              onChange={(event) => {
                setBasicAuth({
                  username: event.target.value,
                  password: basicAuth.password,
                });
                onChange(
                  `${basicPrefix}${window.btoa(
                    `${event.target.value}:${basicAuth.password}`,
                  )}`,
                );
              }}
              placeholder="Username"
              status={FieldWrapper.getStatus(meta)}
            />
          </FieldWrapper>

          <FieldWrapper
            disabled={disabled}
            required
            label="Password"
            meta={meta}
          >
            <InputText
              disabled={disabled}
              value={basicAuth.password}
              onChange={(event) => {
                setBasicAuth({
                  username: basicAuth.username,
                  password: event.target.value,
                });
                onChange(
                  `${basicPrefix}${window.btoa(
                    `${basicAuth.username}:${event.target.value}`,
                  )}`,
                );
              }}
              placeholder="Password"
              status={FieldWrapper.getStatus(meta)}
            />
          </FieldWrapper>
        </FlexBox>
      )}

      {selectedAuthorizationType === 'custom' && (
        <FieldWrapper disabled={disabled} required label="Custom" meta={meta}>
          <InputText
            disabled={disabled}
            status={meta?.error ? 'error' : undefined}
            value={authorization}
            onChange={(event) => {
              setAuthorization(event.target.value);
              onChange(event.target.value);
            }}
            placeholder="Input authorization"
          />
        </FieldWrapper>
      )}

      {selectedAuthorizationType === 'none' && (
        <FlexBox
          alignSelf="center"
          flex="1"
          className={styles.authorizationEmpty}
          margin={isSmallScreen ? '0 0 250 0' : undefined}
        >
          No authorization header added
        </FlexBox>
      )}
    </FlexBox>
  );
};
